import {
  IconArrowRight,
  IconArrowUpRight,
  IconCalendarCheck,
  IconEye,
  IconKey,
  IconMailForward,
  IconPhoneCall,
  IconRefresh,
  IconShieldLock,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type Me } from '../lib/api.js';
import classes from './LandingPage.module.css';
import { Logo } from '../components/Logo.js';

/**
 * Public marketing page.
 *
 * Copy rule followed throughout: say what the software does, in the words a
 * dealership GM would use. No "AI-powered", no "seamless", no "revolutionise".
 * Every claim here is something the system does today — if a feature is built
 * but unproven against a live store, it is described as what it does, never as
 * a result it has produced.
 */

/* -------------------------------------------------------------------------- */
/*  Who is looking                                                            */
/* -------------------------------------------------------------------------- */

/**
 * Whether the visitor already has a session, and where their console is.
 *
 * One call to /admin/me, which answers for both sign-in mechanisms: Clerk's
 * bearer token is attached by the API client when a Clerk session exists, and
 * the password login sends its cookie. Any error at all — 401 for no session,
 * 403 for an identity with no account here — means "show them the way in",
 * so there is nothing to distinguish.
 *
 * Shares the ['me'] query key with the console, so clicking through to the
 * dashboard finds the answer already cached.
 */
function useViewer(): { resolved: boolean; me: Me | null; consoleHref: string } {
  const q = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<Me>('/admin/me'),
    retry: false,
    staleTime: 60_000,
  });

  const me = q.isSuccess ? q.data : null;
  return {
    // Until this is true the auth slot renders empty at a reserved width, so
    // the header never jumps from "Sign in" to "Open dashboard".
    resolved: !q.isPending,
    me,
    // A dealership account belongs in the portal; the console would only show
    // them a page telling them they are in the wrong place.
    consoleHref: me?.kind === 'dealer' ? '/portal' : '/app',
  };
}

/* -------------------------------------------------------------------------- */
/*  Motion primitives                                                         */
/* -------------------------------------------------------------------------- */

/** True once the element has been scrolled into view. Never resets. */
function useInView<T extends HTMLElement>() {
  // Deliberately not annotated: React's own useRef overload returns the
  // RefObject<T> that JSX wants, and writing the type out by hand produces
  // RefObject<T | null>, which it does not accept.
  const ref = useRef<T>(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // No IntersectionObserver must still mean visible content, so the
    // fallback is "shown", never "hidden".
    if (typeof IntersectionObserver === 'undefined') {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShown(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -10% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, shown] as const;
}

function Reveal({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const [ref, shown] = useInView<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`${classes.reveal} ${shown ? classes.revealed : ''}`}
      style={{ transitionDelay: shown ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
}

/**
 * A headline whose lines rise out of a mask, one after another.
 *
 * Each line is split by hand rather than measured, because a measured split
 * reflows on every resize and the whole effect depends on the mask matching
 * the line box exactly.
 */
function RisingLines({ lines, className }: { lines: ReactNode[]; className?: string }) {
  const [ref, shown] = useInView<HTMLHeadingElement>();
  return (
    <h1 ref={ref} className={className}>
      {lines.map((line, i) => (
        <span key={i} className={classes.lineMask}>
          <span
            className={`${classes.line} ${shown ? classes.lineIn : ''}`}
            style={{ transitionDelay: `${i * 90}ms` }}
          >
            {line}
          </span>
        </span>
      ))}
    </h1>
  );
}

/* -------------------------------------------------------------------------- */
/*  Content                                                                   */
/* -------------------------------------------------------------------------- */

const TICKER = [
  'Lead sync',
  'Appointment booking',
  'Email from the store',
  'Call logging',
  'Scoped API keys',
  'Dealer portal',
  'Full request audit',
];

const CHAIN = [
  {
    n: '01',
    title: 'A lead lands in the CRM',
    body: 'We read it within fifteen minutes, straight from CDK. No forms, no exports, no nightly file.',
  },
  {
    n: '02',
    title: 'Your agent calls',
    body: 'Retell dials out with the full context of the enquiry already in hand — name, vehicle, source.',
  },
  {
    n: '03',
    title: 'The appointment is booked',
    body: 'Written back into the dealership’s own CRM, against the real customer record. Four calls, in order.',
  },
  {
    n: '04',
    title: 'Staff see it as normal work',
    body: 'It appears exactly where the sales team already looks. Nothing new to open, nothing new to learn.',
  },
];

const CAPABILITIES = [
  {
    n: '01',
    icon: IconRefresh,
    title: 'Lead sync, on a clock',
    body: 'Every active dealership polled continuously and watermarked per store, so nothing is read twice and nothing slips past the CRM’s seven-day window.',
  },
  {
    n: '02',
    icon: IconCalendarCheck,
    title: 'Real appointment booking',
    body: 'Customer lookup, opportunity creation, then scheduling — the same sequence a service advisor would work through by hand, verified end to end against a live store.',
  },
  {
    n: '03',
    icon: IconMailForward,
    title: 'Email from the dealership',
    body: 'Sent through the store’s own configured sender, so it reaches the customer from a name they already recognise rather than from a platform they have never heard of.',
  },
  {
    n: '04',
    icon: IconPhoneCall,
    title: 'Calls logged as activities',
    body: 'Every conversation your agent has is written back as CRM activity. The audit trail is the CRM itself, which is the one place a dealer principal will actually look.',
  },
  {
    n: '05',
    icon: IconKey,
    title: 'Keys your automations can hold',
    body: 'Scoped to specific dealerships and specific actions, rotatable with a grace period, and idempotent — a retried workflow cannot double-book an appointment or email a customer twice.',
  },
  {
    n: '06',
    icon: IconShieldLock,
    title: 'Isolated per dealership',
    body: 'One store’s credentials cannot reach another’s data. A key is never more powerful than the person who created it, and every outbound call is recorded with its request id.',
  },
];

const FACTS = [
  { n: '15', unit: 'min', label: 'Maximum age of a lead before your agent has it in hand.' },
  { n: '4', unit: 'calls', label: 'What booking one appointment takes against the CRM. All four proven live.' },
  { n: '7', unit: 'days', label: 'How far back CDK will return leads. We watermark against that limit so none are lost.' },
];

const CONTROL = [
  {
    icon: IconShieldLock,
    title: 'Roles, not job titles',
    body: 'Permissions are the unit and roles are named bundles of them. Adding a new kind of user is a row in a table, not a change to the code.',
  },
  {
    icon: IconMailForward,
    title: 'Access by invitation',
    body: 'An account exists because someone deliberately created it. The invitation carries the role and the stores, so accepting grants exactly what was intended.',
  },
  {
    icon: IconEye,
    title: 'See what they see',
    body: 'Borrow a user’s access for thirty minutes to diagnose what they are looking at. Your own name stays on everything you do while you are there.',
  },
];

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

/** Reads as the console without pretending to be a screenshot of it. */
function ConsolePanel() {
  return (
    <div className={classes.panel} aria-hidden="true">
      <div className={classes.panelBar}>
        <span className={classes.panelDots}>
          <i />
          <i />
          <i />
        </span>
        <span className={classes.panelUrl}>liveaios.io/app</span>
      </div>

      <div className={classes.panelBody}>
        <div className={classes.panelHead}>
          <div>
            <p className={classes.panelStore}>Riverside Motors</p>
            <p className={classes.panelMeta}>CDK · subscription linked</p>
          </div>
          <span className={classes.pulse}>
            <i />
            live
          </span>
        </div>

        <div className={classes.panelStats}>
          <div>
            <span className={classes.panelStatNum}>12</span>
            <span className={classes.panelStatLabel}>new leads</span>
          </div>
          <div>
            <span className={classes.panelStatNum}>4</span>
            <span className={classes.panelStatLabel}>booked today</span>
          </div>
          <div>
            <span className={classes.panelStatNum}>8m</span>
            <span className={classes.panelStatLabel}>since last lead</span>
          </div>
        </div>

        <ul className={classes.panelFeed}>
          <li>
            <span className={classes.tagGet}>GET</span>
            elead/v1/leads · 200 · 340 ms
          </li>
          <li>
            <span className={classes.tagPost}>POST</span>
            opportunities · 201 · 512 ms
          </li>
          <li>
            <span className={classes.tagPost}>POST</span>
            activities · 201 · 289 ms
          </li>
        </ul>
      </div>
    </div>
  );
}

export function LandingPage() {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);
  const { resolved, me, consoleHref } = useViewer();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 8);
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(1, y / total) : 0);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  /** The one call to action, which depends entirely on who is looking. */
  function AuthAction({ ghost = false }: { ghost?: boolean }) {
    const cls = ghost ? classes.btnGhost : classes.btn;
    if (!resolved) {
      // Reserved width, no label. Swapping "Sign in" for "Open dashboard" a
      // moment after paint is worse than a brief blank.
      return <span className={`${cls} ${classes.btnPending}`} aria-hidden="true" />;
    }
    return me ? (
      <Link to={consoleHref} className={cls}>
        Open dashboard
        <IconArrowRight size={16} />
      </Link>
    ) : (
      <Link to="/login" className={cls}>
        Sign in
        <IconArrowRight size={16} />
      </Link>
    );
  }

  return (
    <div className={classes.page}>
      {/* Grain and the scroll hairline are decoration; both sit above the page
          and below every interactive thing. */}
      <div className={classes.grain} aria-hidden="true" />
      <div
        className={classes.progress}
        style={{ transform: `scaleX(${progress})` }}
        aria-hidden="true"
      />

      <header className={`${classes.header} ${scrolled ? classes.headerScrolled : ''}`}>
        <div className={`${classes.shell} ${classes.headerInner}`}>
          <Link to="/" className={classes.wordmark}>
            <Logo on="light" height={26} />
          </Link>

          <nav className={classes.nav} aria-label="Main">
            <a className={classes.navLink} href="#how">
              How it works
            </a>
            <a className={classes.navLink} href="#what">
              Capabilities
            </a>
            <a className={classes.navLink} href="#control">
              Control
            </a>
            <a className={classes.navLink} href="#dealers">
              For dealerships
            </a>
          </nav>

          <div className={classes.headerAction}>
            {resolved && me && (
              <span className={classes.whoami} title={me.email}>
                {me.email}
              </span>
            )}
            <AuthAction />
          </div>
        </div>
      </header>

      {/* ------------------------------------------------------------- hero */}
      <section className={`${classes.shell} ${classes.hero}`}>
        <div className={classes.heroCopy}>
          <Reveal>
            <span className={classes.eyebrow}>
              <span className={classes.dot} aria-hidden="true" />
              Built on CDK Fortellis
            </span>
          </Reveal>

          <RisingLines
            className={classes.h1}
            lines={[
              'Your AI agents',
              'work inside the',
              <>
                dealership’s <em>CRM</em>.
              </>,
            ]}
          />

          <Reveal delay={220}>
            <p className={classes.lede}>
              Live AI OS connects voice agents to CDK directly. Leads arrive within minutes,
              appointments are booked against real customer records, and the sales team sees all of
              it in the system they already use — not a separate dashboard nobody opens.
            </p>
          </Reveal>

          <Reveal delay={280}>
            <div className={classes.heroActions}>
              <AuthAction />
              <a href="#how" className={classes.btnGhost}>
                See how it works
              </a>
            </div>
            <p className={classes.heroNote}>
              Dealerships connect through the CDK marketplace. No integration work on their side.
            </p>
          </Reveal>
        </div>

        <Reveal delay={160}>
          <ConsolePanel />
        </Reveal>
      </section>

      {/* ----------------------------------------------------------- ticker */}
      <div className={classes.ticker} aria-hidden="true">
        <div className={classes.tickerTrack}>
          {[0, 1].map((copy) => (
            <div className={classes.tickerRun} key={copy}>
              {TICKER.map((t) => (
                <span key={t} className={classes.tickerItem}>
                  {t}
                  <i className={classes.tickerDot} />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ------------------------------------------------------------ chain */}
      <section id="how" className={classes.chain}>
        <div className={classes.shell}>
          <Reveal>
            <p className={classes.indexLine}>
              <span>(01)</span> How it works
            </p>
          </Reveal>
          <div className={classes.chainInner}>
            {CHAIN.map((s, i) => (
              <Reveal key={s.n} delay={i * 70}>
                <div className={classes.step}>
                  <span className={classes.stepNum}>{s.n}</span>
                  <h3 className={classes.stepTitle}>{s.title}</h3>
                  <p className={classes.stepBody}>{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ----------------------------------------------------- capabilities */}
      <section id="what" className={`${classes.shell} ${classes.section}`}>
        <Reveal>
          <p className={classes.indexLine}>
            <span>(02)</span> Capabilities
          </p>
        </Reveal>
        <Reveal>
          <div className={classes.sectionHead}>
            <h2 className={classes.h2}>
              Everything a conversation needs to <em>become a sale</em>.
            </h2>
            <p className={classes.sectionAside}>
              Each of these is a live path against CDK, not a roadmap item.
            </p>
          </div>
        </Reveal>

        <div className={classes.grid}>
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.title} delay={i * 45}>
              <article className={classes.cell}>
                <div className={classes.cellTop}>
                  <c.icon size={20} stroke={1.7} className={classes.cellIcon} />
                  <span className={classes.cellNum}>{c.n}</span>
                </div>
                <h3 className={classes.cellTitle}>{c.title}</h3>
                <p className={classes.cellBody}>{c.body}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ------------------------------------------------------------ facts */}
      <section className={classes.facts}>
        <div className={`${classes.shell} ${classes.factsInner}`}>
          {FACTS.map((f, i) => (
            <Reveal key={f.n} delay={i * 70}>
              <div className={classes.fact}>
                <p className={classes.factNum}>
                  {f.n}
                  <span className={classes.factUnit}>{f.unit}</span>
                </p>
                <p className={classes.factLabel}>{f.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------------------------------------------------- control */}
      <section id="control" className={classes.dark}>
        <div className={`${classes.shell} ${classes.section}`}>
          <Reveal>
            <p className={`${classes.indexLine} ${classes.indexLineDark}`}>
              <span>(03)</span> Control
            </p>
          </Reveal>
          <Reveal>
            <div className={classes.sectionHead}>
              <h2 className={`${classes.h2} ${classes.h2Dark}`}>
                Who can reach which store is <em>never</em> a guess.
              </h2>
              <p className={`${classes.sectionAside} ${classes.asideDark}`}>
                Every privileged action is recorded against the person who took it — including the
                ones taken while looking through somebody else’s eyes.
              </p>
            </div>
          </Reveal>

          <div className={classes.darkGrid}>
            {CONTROL.map((c, i) => (
              <Reveal key={c.title} delay={i * 60}>
                <div className={classes.darkCell}>
                  <c.icon size={20} stroke={1.7} className={classes.darkIcon} />
                  <h3 className={classes.darkTitle}>{c.title}</h3>
                  <p className={classes.darkBody}>{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------- dealers */}
      <section id="dealers" className={`${classes.shell} ${classes.section}`}>
        <Reveal>
          <p className={classes.indexLine}>
            <span>(04)</span> For dealerships
          </p>
        </Reveal>

        <div className={classes.split}>
          <Reveal>
            <div>
              <h2 className={classes.h2}>
                Their own store, in <em>plain language</em>.
              </h2>
              <p className={classes.lede} style={{ marginTop: 22 }}>
                Dealerships get their own area — not your console with things hidden. They see their
                enquiries, edit their own settings and read the history of what happened to their
                store. Nothing on the page suggests another dealership exists.
              </p>
              <ul className={classes.checklist}>
                <li>Appears in your console on its own once the store activates the app</li>
                <li>Sandbox by default; going live is a deliberate switch you type to confirm</li>
                <li>Every CRM call stored with its request id, status and timing</li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={100}>
            <div className={classes.quote}>
              <p className={classes.quoteMark} aria-hidden="true">
                “
              </p>
              <p className={classes.quoteText}>
                The store never learns a new tool. The appointment simply appears where the sales
                team already looks.
              </p>
              <p className={classes.quoteAttr}>What the integration is for, in one sentence.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* -------------------------------------------------------------- cta */}
      <section className={`${classes.shell} ${classes.cta}`}>
        <Reveal>
          <div className={classes.ctaInner}>
            {/* Three audiences, three sentences. A dealership must never be
                shown console language — their whole area is written to avoid
                implying that other dealerships exist. */}
            <h2 className={`${classes.h2} ${classes.ctaTitle}`}>
              {!me ? (
                <>
                  Ready when your <em>dealerships</em> are.
                </>
              ) : me.kind === 'dealer' ? (
                <>
                  Welcome back. Your <em>store</em> is waiting.
                </>
              ) : (
                <>
                  Welcome back. Your <em>console</em> is waiting.
                </>
              )}
            </h2>
            <p className={classes.ctaLede}>
              {!me
                ? 'Sign in to manage connected stores, watch leads arrive, and check every call the platform has made.'
                : me.kind === 'dealer'
                  ? 'Your enquiries, your settings, and the history of what happened to your dealership.'
                  : 'Connected stores, arriving leads, and every call the platform has made — all in one place.'}
            </p>
            <div className={classes.ctaActions}>
              <AuthAction />
            </div>
          </div>
        </Reveal>
      </section>

      <footer className={classes.footer}>
        <div className={`${classes.shell} ${classes.footerInner}`}>
          <div>
            <Link to="/" className={classes.wordmark}>
              <Logo on="light" height={24} />
            </Link>
            <p className={classes.footerMeta}>
              © {new Date().getFullYear()} — a Next Level Agency product.
            </p>
          </div>

          <div className={classes.footerLinks}>
            <a className={classes.footerLink} href="#how">
              How it works
            </a>
            <a className={classes.footerLink} href="#what">
              Capabilities
            </a>
            <Link className={classes.footerLink} to={me ? consoleHref : '/login'}>
              {me ? 'Dashboard' : 'Sign in'}
              <IconArrowUpRight size={13} />
            </Link>
            <a className={classes.footerLink} href="mailto:faraz@cupupmarketing.com">
              Contact
              <IconArrowUpRight size={13} />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
